import {
  Paper,
  Theme,
  FormControlLabel,
  Switch,
  Button,
  useMediaQuery,
} from "@material-ui/core";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import React, { ReactElement, useEffect } from "react";
import {
  CarMap,
  ImageGallery,
  Footer,
  MakeModelCard,
  StatsCard,
  ImageUpload,
  DamagesFooter,
} from "../components";
import Translator from "../services/Translator";
import { CameraAlt as CameraAltIcon } from "@material-ui/icons";
import clsx from "clsx";
import { useVi, ViActionType } from "../contexts/ViContext";
import { History } from "history";
import { Feature } from "../contexts/FeatureFlags";
import { Thresholds } from "../components/Thresholds";

const useStyles = makeStyles((theme: Theme) => ({
  wrapper: ({ isFooterVisible }: { isFooterVisible: boolean }) => ({
    overflowY: "auto",
    display: "grid",
    gridTemplateColumns: "max(24rem,20vw) minmax(24rem, auto)",
    gridTemplateRows: `1fr minmax(0,min-content)`,
    gridTemplateAreas: '"sideBar gallery" "footer ."',
    height: "100%",
    padding: "0",
    position: "relative",
    "& .gallery": {
      gridArea: "gallery",
      ...(!isFooterVisible ? { gridRowEnd: "span 2" } : {}),
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "100%",
      gridTemplateRows: "auto minmax(auto,30vh) auto",
      gridTemplateAreas: '"sideBar" "footer" "gallery"',
    },
  }),
  sideBar: {
    gridArea: "sideBar",
    position: "relative",
    placeContent: "stretch",
    height: "100%",
    padding: "0.618rem",
    display: "grid",
    boxSizing: "border-box",
    gridGap: "0.382rem",
    gridTemplateRows: "min-content minmax(12rem,min-content) min-content",
    gridTemplateColumns: "auto auto",
    gridTemplateAreas: '"makeModel stats" "carMap carMap" "controls controls"',
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "min-content auto minmax(auto,20rem) min-content",
      gridTemplateRows: "min-content",
      gridTemplateAreas: '"makeModel stats carMap controls"',
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "auto auto",
      gridTemplateRows: "min-content min-content",
      gridTemplateAreas: '"makeModel carMap" "stats stats" "controls controls"',
    },
    "&.loading, &.noCarModel": {
      [theme.breakpoints.up("md")]: {
        gridTemplateRows: "min-content minmax(12rem,min-content) min-content",
        gridTemplateColumns: "auto auto",
        gridTemplateAreas: '"stats stats" "carMap carMap" "controls controls"',
      },
      [theme.breakpoints.down("xs")]: {
        gridTemplateRows: "min-content min-content",
        gridTemplateColumns: "auto",
        gridTemplateAreas: '"stats" "controls"',
      },
    },
  },
  loading: {
    margin: "0.382rem",
    "& svg": {
      width: "100%",
      height: "100%",
      color: theme.palette.secondary.light,
    },
  },
  carMapPanel: {
    gridArea: "carMap",
    display: "flex",
    justifyContent: "center",
    padding: `${theme.goldenRatio(-2)}rem`,
    "& svg": {
      width: "100%",
      height: "auto",
    },
  },
  controls: {
    gridArea: "controls",
    display: "flex",
    alignItems: "center",
    padding: `${theme.goldenRatio(-2)}rem ${theme.goldenRatio(0)}rem`,
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      flexWrap: "wrap",
      justifyContent: "center",
    },
  },
  paper: {
    padding: `${theme.goldenRatio(-2)}rem`,
    margin: `0 ${theme.goldenRatio(-1)}rem`,
    display: "flex",
    justifyContent: "center",
    gridArea: "footer",
  },
  threshold: {
    marginTop: `${theme.goldenRatio(-1)}rem`,
    gridColumn: "1 / span 2",
  },
  switches: {
    minWidth: "max-content",
    flex: "0 1 auto",
    display: "flex",
    flexFlow: "column nowrap",
    "& label > span": {
      fontSize: "0.9rem",
    },
  },
  addPhoto: {
    justifyContent: "center",
    margin: `${theme.goldenRatio(-1)}rem ${theme.goldenRatio(-1)}rem ${theme.goldenRatio(
      -1
    )}rem auto`,
  },
}));

function Case({ history, title }: { history: History; title?: string }): ReactElement {
  const {
    isLoading,
    carModel,
    aggregatedPartDamages,
    claimDamagedParts,
    numberOfDamagedParts,
    meanConfidence,
    images: imageData,
    totalLoss,
    setHoverFilter,
    hoverFilter,
    timing,
    onImageDelete,
    footerTab,
    toggleShowDuplicates,
    toggleShowUndamaged,
    toggleShowRejected,
    dispatch,
    isFilterMatch,
    showDuplicates,
    showUndamaged,
    showRejected,
  } = useVi();
  const theme = useTheme();
  const classes = useStyles({
    isFooterVisible: Boolean(footerTab),
  });

  const canFlip = !useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));

  useEffect(() => {
    const { pathname } = history.location;
    if (history.action === "POP") {
      const matches = pathname.match(/vi\/cases\/([^/]+)\/processings\/([^/]+)/i) || [];
      const [caseId, processingId] = [matches[1], matches[2]];
      if (dispatch) {
        if ((caseId && processingId) || pathname.startsWith("/vi/inference")) {
          dispatch({ type: ViActionType.Reset });
          dispatch({
            type: ViActionType.LoadResultsFromStorage,
            payload: { caseId, processingId },
          });
        } else {
          dispatch && dispatch({ type: ViActionType.Reset });
          history.replace("/");
        }
      }
    }
  }, [history, history.location.pathname, dispatch]);

  useEffect(() => {
    document.title = title || "VI Core";
    const { axManufacturerCodeInt: make, axModelCodeInt: model } = carModel?.model || {};
    if (make) {
      const strings = [
        Translator.translate(`carModels.${make}${model || "00"}.make`, ""),
        Translator.translate(`carModels.${make}${model || "00"}.model`, ""),
      ];
      document.title += ` | ${strings.join(" ")}`;
    }
  }, [title, carModel]);

  const viewPortWidthIsSmDown = useMediaQuery(theme.breakpoints.down("xs"));

  return (
    <div className={classes.wrapper} key={history.location.key}>
      <div
        className={clsx(
          classes.sideBar,
          isLoading && "loading",
          (carModel === undefined || carModel === null) && "noCarModel"
        )}
      >
        {!isLoading && carModel && <MakeModelCard {...{ carModel, isLoading }} />}
        {!(isLoading && viewPortWidthIsSmDown) && (
          <Paper elevation={3} className={classes.carMapPanel}>
            <CarMap
              {...{
                aggregatedPartDamages,
                claimDamagedParts,
                hoverFilter,
                setHoverFilter,
              }}
            />
          </Paper>
        )}
        <StatsCard
          loading={isLoading}
          confidence={meanConfidence}
          imageCount={imageData?.filter((img) => img.status === "ACTIVE").length}
          {...{ numberOfDamagedParts, totalLoss, timing }}
        />
        <Paper elevation={3} className={classes.controls}>
          <div className={classes.switches}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showUndamaged}
                  onChange={toggleShowUndamaged}
                />
              }
              label="Show Undamaged"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showRejected}
                  onChange={toggleShowRejected}
                />
              }
              label="Show Rejected"
            />
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showDuplicates}
                  onChange={toggleShowDuplicates}
                />
              }
              label="Show Duplicates"
            />
          </div>
          <div className={classes.addPhoto}>
            <ImageUpload add>
              <Button
                size="small"
                color="secondary"
                endIcon={<CameraAltIcon />}
                variant="contained"
                style={{ whiteSpace: "nowrap" }}
                disabled={isLoading}
              >
                {Translator.translate("components.imageupload.add_photo")}
              </Button>
            </ImageUpload>
            <Feature name="Thresholds" className={classes.threshold}>
              <Thresholds />
            </Feature>
          </div>
        </Paper>
      </div>
      {useMediaQuery((theme: Theme) => theme.breakpoints.down("sm")) && (
        <Paper elevation={3} className={classes.paper}>
          <DamagesFooter {...{ aggregatedPartDamages, hoverFilter, isFilterMatch }} />
        </Paper>
      )}
      {imageData?.length ? (
        <ImageGallery
          {...{
            onImageDelete,
            hoverFilter,
            setHoverFilter,
            imageData,
            showUndamaged,
            showRejected,
            isLoading,
            canFlip,
          }}
        />
      ) : null}
      {useMediaQuery((theme: Theme) => theme.breakpoints.up("md")) && <Footer />}
    </div>
  );
}

export default React.memo(Case);
